import { HelpBtn } from './HelpBtn';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Save, Trash2, Eye } from 'lucide-react';
import { uid } from '../lib/model';
import { buildGraph, faultZone } from '../lib/netGraph';
import { num } from '../lib/utility';
import { hilite } from './CorrCalc';
import { modeId, ml } from '../lib/profiles';

export const openFault = () => window.dispatchEvent(new Event('giss-fault-open'));
const N = ({ testid, value, ph, onChange }) => <input inputMode="decimal" data-testid={testid} value={value ?? ''} placeholder={ph} onChange={(e) => onChange(e.target.value.replace(',', '.'))} />;
const m = (v, d = 1) => `${(v ?? 0).toFixed(d).replace('.', ',')} m`;
export const faultCalc = (d, slack, tolPct) => { const g = d / (1 + (slack || 0) / 100); return { g, tol: Math.max(1, (g * (tolPct ?? 2)) / 100) }; };

// TDR / OTDR pre-location: measured cable length -> ground distance (slack) -> red search zone on the cable route
export default function CableFault({ t, project, updateProject, mapRef }) {
  const [open, setOpen] = useState(false);
  const heat = modeId() === 'detect_heat';
  const [f, setF] = useState({ s: '', d: '', slack: heat ? '0' : '2.5', tol: heat ? '1' : '2', note: '' });
  useEffect(() => { const on = () => setOpen(true); window.addEventListener('giss-fault-open', on); return () => window.removeEventListener('giss-fault-open', on); }, []);
  const G = useMemo(() => buildGraph(project.pipes, project.fittings, heat ? ['heat'] : ['power', 'telecom']), [project.pipes, project.fittings, heat]);
  const nodes = (project.fittings || []).filter((x) => G.adj['f:' + x.id]);
  const d = num(f.d);
  const calc = f.s && d ? faultCalc(d, num(f.slack), num(f.tol)) : null;
  const z = calc ? faultZone(G, 'f:' + f.s, calc.g, calc.tol) : null;
  useEffect(() => { if (open) hilite(z ? { zones: z.zones, pins: z.pins } : null); }, [open, f]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const close = () => { hilite(null); setOpen(false); };
  const set = (p) => setF((o) => ({ ...o, ...p }));
  const save = () => {
    const sName = project.fittings.find((x) => x.id === f.s)?.name || '?';
    updateProject((p) => ({ ...p, faults: [...(p.faults || []), { id: uid('flt'), start: f.s, startName: sName, d, slack: num(f.slack), tolPct: num(f.tol), ...calc, zones: z.zones, pins: z.pins, note: f.note, t: Date.now() }] }));
    if (z.pins.length) mapRef.current?.fitAll(z.pins);
    toast.success(t.cfSaved);
    close();
  };
  const faults = project.faults || [];
  return createPortal(
    <div className="editor-backdrop" data-testid="fault-dialog">
      <div className="editor cc-ed">
        <div className="row pf-head"><h4>⚡ {ml(t, 'cfTitle')}</h4><HelpBtn k="n:cable_extra" testid="fault-help" title={t.helpHere} /><button className="icon-btn" data-testid="fault-close-btn" onClick={close}><X size={18} /></button></div>
        <div className="sw-grid">
          <label>{ml(t, 'cfStart')}<select data-testid="fault-start" value={f.s} onChange={(e) => set({ s: e.target.value })}>
            <option value="">…</option>{nodes.map((n) => <option key={n.id} value={n.id}>{n.name || n.type}</option>)}
          </select></label>
          <label>{t.cfDist} (m)<N testid="fault-dist" value={f.d} ph="142.5" onChange={(v) => set({ d: v })} /></label>
          <label>{t.cfSlack} (%)<N testid="fault-slack" value={f.slack} ph="2.5" onChange={(v) => set({ slack: v })} /></label>
          <label>{t.cfTol} (± %)<N testid="fault-tol" value={f.tol} ph="2" onChange={(v) => set({ tol: v })} /></label>
          <label>{t.fitNote}<input data-testid="fault-note" value={f.note} onChange={(e) => set({ note: e.target.value })} /></label>
        </div>
        {!nodes.length && <p className="muted" data-testid="fault-no-nodes">{ml(t, 'cfNoNodes')}</p>}
        {calc && <p className="sw-res" data-testid="fault-result">{t.cfGround}: <b>{m(calc.g, 2)}</b> · {t.cfZone}: <b>{m(calc.g - calc.tol)} – {m(calc.g + calc.tol)}</b> (± {m(calc.tol, 2)}) · {z.pins.length} {t.cfBranches}</p>}
        {calc && !z.pins.length && <p className="pr-alert low" data-testid="fault-beyond">{t.cfBeyond.replace('{x}', m(z.reach))}</p>}
        <button className="btn btn-red" data-testid="fault-save-btn" disabled={!z?.zones.length} onClick={save}><Save size={16} /> {t.cfSave}</button>
        {faults.length > 0 && <div className="ut-list" data-testid="fault-list">{faults.map((x, i) => (
          <div className="row" key={x.id} data-testid={`fault-item-${i}`}>
            <small>{new Date(x.t).toLocaleString()} · {x.startName} · {m(x.d)} → {m(x.g, 2)} ± {m(x.tol, 1)}</small>
            <button className="icon-btn" data-testid={`fault-show-${i}`} onClick={() => { mapRef.current?.fitAll(x.pins.length ? x.pins : x.zones.flat()); close(); }}><Eye size={14} /></button>
            <button className="icon-btn" data-testid={`fault-del-${i}`} onClick={() => updateProject((p) => ({ ...p, faults: p.faults.filter((y) => y.id !== x.id) }))}><Trash2 size={14} /></button>
          </div>
        ))}</div>}
      </div>
    </div>,
    document.body,
  );
}
